// =====================================================
// HOOK: useTimeline
// Description: Hook pour gérer les événements timeline d'une entité avec pagination
// =====================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSupabase } from './useSupabase';
import type { TimelineEvent, CreateTimelineEventDto, EntityType } from '../types/timeline';
import { monitorQuery } from '../lib/supabase-monitor';
import { useUsers } from '../contexts/UsersContext';

// ✅ PAGINATION : Nombre d'événements par page
const EVENTS_PER_PAGE = 50;

export function useTimeline(entityType: EntityType, entityId: string) {
  const supabase = useSupabase();
  const { usersMap } = useUsers(); // ✅ Cache des utilisateurs
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const offsetRef = useRef(0); // ✅ Utiliser useRef au lieu d'un état
  const [hasMore, setHasMore] = useState(false);

  // Récupérer les événements de la timeline avec pagination
  const fetchEvents = useCallback(
    async (append: boolean = false) => {
      try {
        // Ne mettre loading à true que pour le chargement initial
        if (!append) {
          setLoading(true);
        }
        setError(null);

        const currentOffset = append ? offsetRef.current : 0;

        // 1. Récupérer les événements avec pagination
        // 📊 MONITORED: Récupération timeline events
        const { data, error: fetchError } = await monitorQuery(
          'timeline_events',
          'select',
          `${entityType}/${entityId}`,
          () =>
            supabase
              .from('timeline_events')
              .select('*')
              .eq('entity_type', entityType)
              .eq('entity_id', entityId)
              .order('created_at', { ascending: false })
              .range(currentOffset, currentOffset + EVENTS_PER_PAGE - 1),
          'useTimeline.fetchEvents'
        );

        if (fetchError) throw fetchError;

        const fetchedEvents = (data as any[]) || [];

        // 2. Enrichir directement avec le cache des utilisateurs
        // ✅ OPTIMISATION : Plus besoin d'appeler get_users_list à chaque fois !
        const enrichedEvents = fetchedEvents.map((event) => ({
          ...event,
          created_by_user:
            event.created_by && usersMap[event.created_by] ? usersMap[event.created_by] : null,
        }));

        // 3. Déterminer s'il y a plus d'événements
        setHasMore(fetchedEvents.length === EVENTS_PER_PAGE);

        // 4. Ajouter ou remplacer les événements
        if (append) {
          setEvents((prev) => [...prev, ...enrichedEvents]);
          offsetRef.current = currentOffset + fetchedEvents.length;
        } else {
          setEvents(enrichedEvents);
          offsetRef.current = fetchedEvents.length;
        }
      } catch (err) {
        console.error('Erreur lors du chargement de la timeline:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [supabase, entityType, entityId, usersMap]
  );

  // Charger les événements au montage du composant
  useEffect(() => {
    fetchEvents(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger plus d'événements (pagination)
  const loadMore = useCallback(async () => {
    if (!loading && hasMore) {
      await fetchEvents(true);
    }
  }, [loading, hasMore, fetchEvents]);

  // Créer un nouvel événement
  const createEvent = useCallback(
    async (dto: Omit<CreateTimelineEventDto, 'entity_type' | 'entity_id'>) => {
      try {
        const { data, error: createError } = await supabase
          .from('timeline_events')
          .insert({
            entity_type: entityType,
            entity_id: entityId,
            ...dto,
          })
          .select()
          .single();

        if (createError) throw createError;

        // Rafraîchir la liste des événements (reset offset et recharge le premier lot)
        offsetRef.current = 0;
        await fetchEvents(false);

        return data as TimelineEvent;
      } catch (err) {
        console.error("Erreur lors de la création de l'événement timeline:", err);
        throw err;
      }
    },
    [supabase, entityType, entityId, fetchEvents]
  );

  // Rafraîchir manuellement la liste (reset offset et recharge le premier lot)
  const refresh = useCallback(async () => {
    offsetRef.current = 0;
    await fetchEvents(false);
  }, [fetchEvents]);

  return {
    events,
    loading,
    error,
    hasMore,
    createEvent,
    refresh,
    loadMore,
  };
}
